import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { categories, articles, faqs } from '@/mocks/data';

export default function Home() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '糖心视频',
    url: 'https://www.tangxinshipin.com/',
    description: '糖心视频汇聚蜜桃视频、桃子视频等甜蜜水果品牌，提供超清画质、流畅播放的在线观影体验，苹果、榴莲、芒果视频高清免费看。',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://www.tangxinshipin.com/search?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '糖心视频',
    url: 'https://www.tangxinshipin.com/',
    description: '糖心视频汇聚蜜桃视频、桃子视频等甜蜜水果品牌，提供超清画质、流畅播放的在线观影体验。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6688',
      contactType: 'customer service',
      email: 'support@tangxinshipin.com',
    },
    sameAs: [
      'https://weibo.com/tangxinshipin',
      'https://www.douyin.com/user/tangxinshipin',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title="糖心视频 - 蜜桃视频桃子视频甜蜜水果共赏高清在线 | 苹果榴莲芒果视频超清画质极速播放体验"
        description="糖心视频汇聚蜜桃视频、桃子视频等甜蜜水果品牌，提供超清画质、流畅播放的在线观影体验，苹果、榴莲、芒果视频高清免费看。糖心视频致力于打造极致观影平台，海量精彩内容每日更新。"
        keywords="糖心视频, 蜜桃视频, 桃子视频, 苹果视频, 榴莲视频, 芒果视频, 甜蜜视频, 糖果视频, 高清在线, 超清画质, 极速播放"
        canonicalUrl="https://www.tangxinshipin.com/"
        schemaData={{ ...homepageSchema, ...orgSchema, ...faqSchema }}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20sweet%20candy%20and%20fruit%20themed%20background%20with%20soft%20gradients%20of%20peach%20coral%20pink%20and%20golden%20tones%2C%20dreamy%20atmospheric%20bokeh%20effects%2C%20smooth%20flowing%20curves%2C%20cinematic%20lighting%2C%20warm%20and%20inviting%20mood%2C%20minimalist%20composition%2C%20soft%20focus%20background%20with%20gentle%20highlights&width=1600&height=900&seq=hero-tangxin&orientation=landscape"
              alt="糖心视频 - 甜蜜观影体验"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>

          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <span className="inline-block px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 text-xs md:text-sm font-medium mb-6">
                汇聚六大甜蜜品牌 · 超清画质极速播放
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
                甜蜜观影，<br className="md:hidden" />幸福每一刻
              </h1>
              <p className="text-background-50/85 text-sm md:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
                糖心视频汇聚蜜桃视频、桃子视频、苹果视频、榴莲视频、芒果视频和糖果视频六大品牌，为您带来超清画质、极速播放的极致观影体验。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category"
                  className="whitespace-nowrap cursor-pointer px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-all duration-200 hover:scale-105"
                >
                  立即探索
                </Link>
                <Link
                  to="/about"
                  className="whitespace-nowrap cursor-pointer px-8 py-3 rounded-full border border-background-50/30 text-background-50 text-sm md:text-base font-medium hover:bg-background-50/15 transition-all duration-200"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="flex items-center gap-2 text-background-50/60 text-xs">
              <span>向下滚动</span>
              <i className="ri-arrow-down-line animate-bounce"></i>
            </div>
          </div>
        </section>

        <section className="bg-primary-500 py-6 md:py-8">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-4xl mx-auto">
              {[
                { value: '2亿+', label: '累计用户' },
                { value: '10万+', label: '高清影片' },
                { value: '6大', label: '品牌矩阵' },
                { value: '500+', label: 'CDN节点' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                    {stat.value}
                  </div>
                  <div className="text-background-50/75 text-xs md:text-sm mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                甜蜜品牌矩阵
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
                六大水果品牌各具特色，总有一款适合你
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category?tab=${cat.slug}`}
                  className="group cursor-pointer bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="text-background-50/80 text-xs">
                        {cat.count.toLocaleString()} 部影片
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-foreground-500 text-xs md:text-sm leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-end justify-between mb-10 md:mb-14 max-w-6xl mx-auto">
              <div>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  热门文章
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  最新观影资讯与精彩内容推荐
                </p>
              </div>
              <Link
                to="/category"
                className="whitespace-nowrap cursor-pointer hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto">
              {articles.slice(0, 6).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group cursor-pointer bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-44 md:h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="inline-block px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                        {article.categoryName}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category"
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-1 text-sm text-primary-500 font-medium"
              >
                查看全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                即刻开启甜蜜观影之旅
              </h2>
              <p className="text-foreground-500 text-sm md:text-base mb-8 max-w-lg mx-auto">
                加入糖心视频，畅享六大品牌海量高清内容，超清画质极速播放，随时随地想看就看。
              </p>
              <Link
                to="/category"
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-medium hover:bg-primary-600 transition-all duration-200 hover:scale-105"
              >
                <i className="ri-play-circle-line text-lg"></i>
                开始观影
              </Link>
            </div>
          </div>
        </section>

        <section id="faq" className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                关于糖心视频，你想知道的都在这里
              </p>
            </div>

            <div className="max-w-2xl mx-auto flex flex-col gap-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 cursor-pointer text-left"
                    onClick={() => toggleFaq(index)}
                  >
                    <span className="font-medium text-foreground-800 text-sm md:text-base pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 text-lg flex-shrink-0 ${
                        openFaqIndex === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaqIndex === index ? 'max-h-96 pb-4 px-5' : 'max-h-0'
                    }`}
                  >
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}